import type {
  AgentCard,
  AgentRole,
  BobState,
  BuildDirective,
  BuildIntake,
  PipelineStage,
} from "../../types";
import { uid } from "../storage";

export const PIPELINE_STAGES: PipelineStage[] = [
  "intake",
  "decomposition",
  "architecture",
  "codeBuild",
  "testRepair",
  "packaging",
  "handoff",
];

export const STAGE_LABELS: Record<PipelineStage, string> = {
  intake: "Intake",
  decomposition: "Decomposition",
  architecture: "Architecture",
  codeBuild: "Code Build",
  testRepair: "Test & Repair",
  packaging: "Packaging",
  handoff: "Handoff",
};

export function stageIndex(stage: PipelineStage): number {
  return PIPELINE_STAGES.indexOf(stage);
}

export function progressForStage(stage: PipelineStage): number {
  const idx = stageIndex(stage);
  if (idx <= 0) return 0;
  if (idx >= PIPELINE_STAGES.length - 1) return 100;
  return Math.round((idx / (PIPELINE_STAGES.length - 1)) * 100);
}

export function createDirectiveFromIntake(intake: BuildIntake): BuildDirective {
  const now = new Date().toISOString();
  return {
    id: uid("directive"),
    intakeId: intake.id,
    name: intake.projectName,
    objective: `Deliver ${intake.targetOutput} on ${intake.stackPreference}. ${intake.constraints}`,
    stage: "intake",
    progress: 0,
    priority: "medium",
    assignedRole: "bob",
    paused: false,
    createdAt: now,
    updatedAt: now,
  };
}

export function advanceStage(directive: BuildDirective): BuildDirective {
  if (directive.paused) return directive;
  const idx = stageIndex(directive.stage);
  const now = new Date().toISOString();
  if (idx >= PIPELINE_STAGES.length - 1) {
    return { ...directive, progress: 100, updatedAt: now };
  }
  const nextStage = PIPELINE_STAGES[idx + 1];
  return {
    ...directive,
    stage: nextStage,
    progress: progressForStage(nextStage),
    updatedAt: now,
  };
}

export function pauseDirective(directive: BuildDirective): BuildDirective {
  return { ...directive, paused: true, updatedAt: new Date().toISOString() };
}

export function resumeDirective(directive: BuildDirective): BuildDirective {
  return { ...directive, paused: false, updatedAt: new Date().toISOString() };
}

export function assignRole(directive: BuildDirective, role: AgentRole): BuildDirective {
  return { ...directive, assignedRole: role, updatedAt: new Date().toISOString() };
}

export function updateDirective(
  directives: BuildDirective[],
  id: string,
  updater: (d: BuildDirective) => BuildDirective,
): BuildDirective[] {
  return directives.map((d) => (d.id === id ? updater(d) : d));
}

export interface DeckMetrics {
  activeBuilds: number;
  queued: number;
  completed: number;
  agentsEngaged: number;
  openFailures: number;
  totalDirectives: number;
  avgProgress: number;
}

export function computeDeckMetrics(state: BobState): DeckMetrics {
  const { directives, agents, failures } = state;
  const activeBuilds = directives.filter((d) => !d.paused && d.stage !== "handoff").length;
  const queued = directives.filter((d) => d.paused).length;
  const completed = directives.filter((d) => d.stage === "handoff" && d.progress >= 100).length;
  const agentsEngaged = agents.filter((a) => a.status === "active").length;
  const openFailures = failures.filter(
    (f) => f.status === "open" || f.status === "repairing",
  ).length;
  const avgProgress =
    directives.length === 0
      ? 0
      : Math.round(directives.reduce((sum, d) => sum + d.progress, 0) / directives.length);

  return {
    activeBuilds,
    queued,
    completed,
    agentsEngaged,
    openFailures,
    totalDirectives: directives.length,
    avgProgress,
  };
}

export function seedDemoDirectives(): BuildDirective[] {
  const now = new Date().toISOString();
  return [
    {
      id: "directive_demo_1",
      name: "BOB v2 Pipeline Upgrade",
      objective: "Add command deck, pipeline board, agent recovery, and portfolio mode surfaces.",
      stage: "codeBuild",
      progress: 58,
      priority: "high",
      assignedRole: "felix",
      paused: false,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: "directive_demo_2",
      name: "Cloudflare Worker Export",
      objective: "Package sovereign runtime handoff for bob.arkmpyre.com deploy.",
      stage: "packaging",
      progress: 82,
      priority: "medium",
      assignedRole: "lisa",
      paused: false,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: "directive_demo_3",
      name: "Legacy Merge Salvage",
      objective: "Composite Candidate A + B with deterministic conflict patches.",
      stage: "testRepair",
      progress: 45,
      priority: "critical",
      assignedRole: "executor",
      paused: true,
      createdAt: now,
      updatedAt: now,
    },
  ];
}

export function seedAgentCards(): AgentCard[] {
  return [
    {
      id: "agent_bob",
      role: "bob",
      displayName: "BOB",
      status: "active",
      currentDirectiveId: "directive_demo_1",
      taskSummary: "Orchestrating v2 surface integration",
      utilization: 72,
    },
    {
      id: "agent_felix",
      role: "felix",
      displayName: "Felix",
      status: "active",
      currentDirectiveId: "directive_demo_1",
      taskSummary: "Building pipeline components",
      utilization: 88,
    },
    {
      id: "agent_lisa",
      role: "lisa",
      displayName: "Lisa",
      status: "active",
      currentDirectiveId: "directive_demo_2",
      taskSummary: "Packaging export artifacts",
      utilization: 64,
    },
    {
      id: "agent_executor",
      role: "executor",
      displayName: "Executor",
      status: "blocked",
      currentDirectiveId: "directive_demo_3",
      taskSummary: "Awaiting merge conflict resolution",
      utilization: 35,
    },
  ];
}

export function syncAgentsWithDirectives(
  agents: AgentCard[],
  directives: BuildDirective[],
): AgentCard[] {
  return agents.map((agent) => {
    const assigned = directives.find(
      (d) => d.assignedRole === agent.role && !d.paused && d.stage !== "handoff",
    );
    if (!assigned) {
      return {
        ...agent,
        status: "idle",
        currentDirectiveId: undefined,
        taskSummary: "Standing by for assignment",
        utilization: Math.max(10, agent.utilization - 20),
      };
    }
    return {
      ...agent,
      status: assigned.paused ? "blocked" : "active",
      currentDirectiveId: assigned.id,
      taskSummary: `${STAGE_LABELS[assigned.stage]} — ${assigned.name}`,
      utilization: Math.min(100, assigned.progress + 15),
    };
  });
}
